// Binds the data registry to the editor's DuckDB instance and the
// current folder source. main.ts attaches a source when a folder loads;
// the runner calls prepareRun before every run; the tree calls
// ensureDataLoaded before opening the viewer.
//
// Local sources hand out browser File objects, registered as lazy
// handles. Sources without `localFile` (the cloud vault) supply bytes
// on demand through `readBytes`, which is the download-and-decrypt path.

import { DuckDBDataProtocol } from '@duckdb/duckdb-wasm';
import { getDuckDB, getSessionId, hasActiveSession } from './duckdb';
import { DataRegistry, type DataEntry } from './data-registry';
import { scanQuotedPaths } from './data-kinds';
import type { FileEntry, FolderSource } from './fs';

const registry = new DataRegistry({
  async registerHandle(name, file) {
    const db = await getDuckDB();
    await db.registerFileHandle(name, file, DuckDBDataProtocol.BROWSER_FILEREADER, true);
  },
  async registerBuffer(name, bytes) {
    const db = await getDuckDB();
    await db.registerFileBuffer(name, bytes);
  },
  async drop(names) {
    // Never instantiate DuckDB just to forget names it never saw.
    if (!hasActiveSession()) return;
    const db = await getDuckDB();
    await db.dropFiles(names);
  },
  sessionId: getSessionId,
});

/// Point the registry at the data entries of a freshly loaded source.
/// Registration itself waits for the first run or viewer open, so
/// opening a folder never triggers the DuckDB download.
export function attachDataSource(source: FolderSource, entries: FileEntry[]): Promise<void> {
  const local = source.localFile ? source.localFile.bind(source) : null;
  const data = entries
    .filter((e) => e.kind === 'data')
    .map((e): DataEntry => (local
      ? { path: e.path, kind: 'handle', file: () => local(e.path) }
      : { path: e.path, kind: 'buffer', bytes: () => source.readBytes(e.path) }));
  return registry.replaceAll(data);
}

export function detachDataSource(): Promise<void> {
  return registry.replaceAll([]);
}

/// Before a run: register every local handle plus the on-demand files
/// the program names in quoted literals. Returns what was registered.
export function prepareRun(program: string): Promise<string[]> {
  const referenced = scanQuotedPaths(program, registry.paths());
  return registry.ensureLoaded(referenced);
}

/// Before the viewer opens a data file.
export function ensureDataLoaded(path: string): Promise<string[]> {
  return registry.ensureLoaded([path]);
}

export function isDataLoaded(path: string): boolean {
  return registry.isLoaded(path);
}

export function onDataChange(fn: () => void): () => void {
  return registry.onChange(fn);
}
